/* Public Klaviyo client API only. Attendance is set by staff, never by this form. */
(() => {
  'use strict';
  const normalizePhone = value => {
    const raw = String(value || '').trim();
    const digits = raw.replace(/\D/g, '');
    if (raw.startsWith('+') && /^[1-9]\d{9,14}$/.test(digits)) return `+${digits}`;
    if (/^\d{10}$/.test(digits)) return `+1${digits}`;
    if (/^1\d{10}$/.test(digits)) return `+${digits}`;
    return '';
  };
  const clean = (value, max = 150) => String(value || '').trim().replace(/\s+/g, ' ').slice(0, max);
  function buildPayload(config, entry) {
    if (config.listId === 'PREVIEW') throw new Error('This preview does not accept registrations. The signup form will open when setup is complete.');
    if (!/^[a-z][a-z0-9_]{2,70}$/.test(config.eventKey)) throw new Error('This registration is not configured. Please contact Pictureline.');
    if (!/^[a-zA-Z0-9]+$/.test(config.listId) || !/^[a-zA-Z0-9]+$/.test(config.companyId)) throw new Error('Registration is temporarily unavailable. Please contact Pictureline.');
    const first = clean(entry.firstName, 100), last = clean(entry.lastName, 100);
    if (!first || !last) throw new Error('Please enter your first and last name.');
    const properties = {
      [`${config.eventKey}__registered`]: true,
      [`${config.eventKey}__registered_at`]: entry.registeredAt,
      [`${config.eventKey}__contact_method`]: entry.contactMethod,
      [`${config.eventKey}__event`]: config.eventName,
      [`${config.eventKey}__source`]: config.source
    };
    const seen = new Set(['registered','registered_at','contact_method','event','source','attendance','attended','checked_in_at']);
    for (const field of entry.fields) {
      if (!/^[a-z][a-z0-9_]{0,60}$/.test(field.key) || seen.has(field.key)) throw new Error('A signup field needs attention. Please contact Pictureline.');
      seen.add(field.key);
      const value = clean(field.value, 1000);
      if (field.required && !value) throw new Error(`Please complete ${field.label}.`);
      properties[`${config.eventKey}__${field.key}`] = value;
    }
    const attributes = {first_name:first, last_name:last, properties, subscriptions:{}};
    if (entry.contactMethod === 'sms') {
      const phone = normalizePhone(entry.phone);
      if (!phone) throw new Error('Please enter a valid mobile number, including the area code.');
      if (!/^\+1\d{10}$/.test(phone)) throw new Error('Text reminders are available for U.S. and Canadian mobile numbers. Please choose email for another country.');
      if (!entry.smsConsent) throw new Error('Please select the text-reminder consent box, or choose email instead.');
      attributes.phone_number = phone;
      attributes.subscriptions.sms = {transactional:{consent:'SUBSCRIBED'}};
    } else if (entry.contactMethod === 'email') {
      const email = String(entry.email || '').trim();
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) throw new Error('Please enter a valid email address.');
      attributes.email = email;
      attributes.subscriptions.email = {marketing:{consent:'SUBSCRIBED'}};
    } else throw new Error('Please choose email or text for your event reminder.');
    return {data:{type:'subscription', attributes:{custom_source:config.source, profile:{data:{type:'profile',attributes}}}, relationships:{list:{data:{type:'list',id:config.listId}}}}};
  }
  async function sendRegistration(companyId, payload, {fetchImpl = fetch, timeoutMs = 20000} = {}) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), timeoutMs);
    try {
      const response = await fetchImpl(`https://a.klaviyo.com/client/subscriptions?company_id=${encodeURIComponent(companyId)}`, {method:'POST', headers:{accept:'application/vnd.api+json','content-type':'application/vnd.api+json',revision:'2026-07-15'},body:JSON.stringify(payload),signal:controller.signal});
      if (!response.ok) throw new Error(response.status === 429 ? 'Registration is busy right now. Please wait a moment and try again.' : 'We couldn’t save your registration. Please check your details and try again.');
    } finally {clearTimeout(timeout);}
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = {normalizePhone, buildPayload, sendRegistration};
  if (typeof document === 'undefined') return;
  function init(root) {
    root.querySelectorAll('[data-event-registration]').forEach(form => {
      if (form.dataset.initialized) return;
      form.dataset.initialized = 'true';
      const status = form.querySelector('[data-status]');
      const button = form.querySelector('[data-submit]');
      const initialLabel = button.textContent;
      let busy = false;
      const setStatus = (message, state) => {status.textContent = message; status.dataset.state = state;};
      function syncContact() {
        const method = form.elements.contact_method.value;
        form.querySelectorAll('[data-contact-panel]').forEach(panel => {
          const active = panel.dataset.contactPanel === method;
          panel.hidden = !active;
          panel.querySelectorAll('input').forEach(input => {input.disabled = !active; input.required = active;});
        });
      }
      form.querySelectorAll('[name="contact_method"]').forEach(input => input.addEventListener('change', syncContact));
      syncContact();
      form.querySelectorAll('[data-field-key]').forEach(wrapper => {
        const select = wrapper.querySelector('select');
        const panel = wrapper.querySelector('[data-other-panel]');
        if (!select || !panel) return;
        const input = panel.querySelector('input');
        function syncOther() {const other = select.value === '__other__'; panel.hidden = !other; input.disabled = !other; input.required = other;}
        select.addEventListener('change', () => {syncOther(); if (!panel.hidden) input.focus();});
        syncOther();
      });
      form.addEventListener('submit', async event => {
        event.preventDefault();
        if (busy || !form.reportValidity()) return;
        if (form.elements.website.value) return;
        let payload;
        try {
          const fields = Array.from(form.querySelectorAll('[data-field-key]'), wrapper => {
            const input = wrapper.querySelector('[data-field-value]');
            const isOther = input.value === '__other__';
            return {key:wrapper.dataset.fieldKey, label:wrapper.querySelector('label').textContent.replace('*','').trim(), required:input.required || isOther, value:isOther ? wrapper.querySelector('[data-other-value]').value : input.value};
          });
          payload = buildPayload(form.dataset, {firstName:form.elements.first_name.value, lastName:form.elements.last_name.value, email:form.elements.email.value, phone:form.elements.phone_number?.value, contactMethod:form.elements.contact_method.value, smsConsent:form.elements.sms_consent?.checked, fields, registeredAt:new Date().toISOString()});
        } catch(error) {setStatus(error.message, 'error'); status.focus(); return;}
        busy = true; button.disabled = true; button.textContent = 'Saving your registration…'; setStatus('', 'pending');
        try {
          await sendRegistration(form.dataset.companyId, payload);
          form.querySelector('.event-page__fields').hidden = true;
          setStatus(form.dataset.successMessage, 'success'); status.focus();
        } catch(error) {
          setStatus(error.name === 'AbortError' ? 'We couldn’t confirm your registration. Please try again; your details are still here.' : (error instanceof TypeError ? 'Please check your internet connection and try again. Your details are still here.' : error.message), 'error');
          button.disabled = false; button.textContent = initialLabel; busy = false; status.focus();
        }
      });
    });
  }
  init(document);
  document.addEventListener('shopify:section:load', event => init(event.target));
})();
